"use client";

import { format } from "date-fns";
import { CalendarIcon, ClockIcon } from "lucide-react";
import type React from "react";
import { useCallback, useState } from "react";
import { cn } from "../../lib/utils";
import { omitUndefined } from "../../lib/props";
import { Button } from "./button";
import { Calendar } from "./calendar";
import { InputGroup, InputGroupAddon, InputGroupInput } from "./input-group";
import { Popover, PopoverPopup, PopoverTrigger } from "./popover";
import type { Omittable } from "../../lib/props";

export type DatePickerMode = "date" | "datetime" | "time";

/** date-fns pattern for the trigger label, per mode. */
const TRIGGER_FORMAT: Record<DatePickerMode, string> = {
  date: "MMM d, yyyy",
  datetime: "MMM d, yyyy h:mm a",
  time: "h:mm a",
};

const MODE_PLACEHOLDER: Record<DatePickerMode, string> = {
  date: "Pick a date",
  datetime: "Pick a date & time",
  time: "Pick a time",
};

/** Hides the browser's own picker affordance so the time is typed, never
 * picked — the whole point of owning this control. */
const TIME_INPUT_CLASS =
  "*:[input]:[&::-webkit-calendar-picker-indicator]:hidden *:[input]:[&::-webkit-calendar-picker-indicator]:appearance-none";

/** `HH:mm` for the time input; "" when there is no value. */
function toTimeInputValue(date: Date | null): string {
  if (!date) return "";
  const h = String(date.getHours()).padStart(2, "0");
  const m = String(date.getMinutes()).padStart(2, "0");
  return `${h}:${m}`;
}

export type DatePickerProps = Omittable<{
  /** `date` is a calendar, `time` a typed clock field, `datetime` both. */
  mode?: DatePickerMode;
  /** The selected moment, or null when empty. In `time` mode only the
   * hours/minutes are meaningful — the date rides along at the epoch day. */
  value: Date | null;
  onValueChange: (date: Date | null) => void;
  placeholder?: string;
  /** Trigger scale — the Button scale. */
  size?: "sm" | "default" | "lg";
  /** Trigger face. `outline` reads as a form field; `ghost` is chromeless,
   * for inline rows that carry their own borders. */
  variant?: "outline" | "ghost";
  disabled?: boolean;
  /** Matcher for days that cannot be picked. */
  disabledDates?: (date: Date) => boolean;
  align?: React.ComponentProps<typeof PopoverPopup>["align"];
  /** date-fns pattern for the trigger label. Defaults per mode. */
  dateFormat?: string;
  /** For an external `<FieldLabel htmlFor>`. */
  id?: string;
  ariaLabel?: string;
  /** Controlled open state. Omit and the picker owns it. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Custom trigger chrome — a `PopoverTrigger` subtree, so the picker can
   * hang off a host's own control (an inline cell, a table cell) instead of
   * the default Button. Omit for the default trigger. */
  children?: React.ReactNode;
}>;

/**
 * Date / time picker: a `Popover` whose popup is a `Calendar`, a typed time
 * field, or both. It never falls through to the browser's native picker.
 *
 * `date` closes on select. `datetime` does not — the day is only half the
 * value, so the popup stays up for the time. There is deliberately no range
 * mode: a range is two of these.
 */
export function DatePicker({
  align = "start",
  ariaLabel,
  children,
  dateFormat,
  disabled,
  disabledDates,
  id,
  mode = "date",
  onOpenChange,
  onValueChange,
  open: openProp,
  placeholder,
  size = "default",
  value,
  variant = "outline",
}: DatePickerProps): React.ReactElement {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = openProp ?? uncontrolledOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );

  const handleSelectDay = useCallback(
    (day: Date | undefined) => {
      if (!day) {
        onValueChange(null);
        return;
      }
      const next = new Date(day);
      if (mode === "datetime") {
        // Keep the time already on the value; a first pick starts at midnight.
        next.setHours(value?.getHours() ?? 0, value?.getMinutes() ?? 0, 0, 0);
        onValueChange(next);
        // Deliberately stays open — the time half is still unset.
        return;
      }
      onValueChange(next);
      setOpen(false);
    },
    [mode, onValueChange, setOpen, value],
  );

  const handleTimeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value;
      if (!raw) return;
      const [hours, minutes] = raw.split(":").map(Number);
      // A short `12` leaves `minutes` missing. A missing field is not NaN, so
      // it needs its own rejection rather than reaching `setHours` as one.
      if (hours === undefined || minutes === undefined) return;
      if (Number.isNaN(hours) || Number.isNaN(minutes)) return;
      // A time with no date yet anchors to the epoch day in `time` mode (the
      // conventional time-only carrier) and to today in `datetime` mode.
      const base =
        value ?? (mode === "time" ? new Date(1970, 0, 1) : new Date());
      const next = new Date(base);
      next.setHours(hours, minutes, 0, 0);
      onValueChange(next);
    },
    [mode, onValueChange, value],
  );

  const label = value
    ? format(value, dateFormat ?? TRIGGER_FORMAT[mode])
    : (placeholder ?? MODE_PLACEHOLDER[mode]);

  return (
    <Popover onOpenChange={setOpen} open={open}>
      {children ?? (
        <PopoverTrigger
          aria-label={ariaLabel}
          id={id}
          render={
            <Button
              className="w-full justify-start font-normal"
              disabled={disabled}
              size={size}
              variant={variant}
            />
          }
        >
          {mode === "time" ? (
            <ClockIcon aria-hidden="true" />
          ) : (
            <CalendarIcon aria-hidden="true" />
          )}
          <span
            className={cn(
              "min-w-0 flex-1 truncate text-left",
              !value && "text-muted-foreground/72",
            )}
          >
            {label}
          </span>
        </PopoverTrigger>
      )}
      <PopoverPopup align={align}>
        <div className="flex flex-col gap-2">
          {mode !== "time" && (
            <Calendar
              mode="single"
              onSelect={handleSelectDay}
              {...omitUndefined({
                defaultMonth: value ?? undefined,
                disabled: disabledDates,
                selected: value ?? undefined,
              })}
            />
          )}
          {mode !== "date" && (
            <InputGroup className={TIME_INPUT_CLASS}>
              <InputGroupInput
                aria-label="Enter time"
                onChange={handleTimeChange}
                type="time"
                value={toTimeInputValue(value)}
              />
              <InputGroupAddon>
                <ClockIcon aria-hidden="true" />
              </InputGroupAddon>
            </InputGroup>
          )}
        </div>
      </PopoverPopup>
    </Popover>
  );
}
